import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiService } from "@/lib/api-functions/api-service";
import { generateCleanFilename, downloadJSON } from "./utils";

interface Game {
  id: string;
  name: string;
}

export function useIndividualExports() {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportData = async (
    fetchFn: (gameId: string) => Promise<any>,
    game: Game,
    dataType: string,
    pluralLabel: string
  ) => {
    setIsExporting(true);
    try {
      const result = await fetchFn(game.id);
      if (result.success && result.data) {
        const filename = generateCleanFilename(game.name, dataType);
        downloadJSON(result.data, filename);
        toast({
          title: `${pluralLabel} Exported`,
          description: `${result.data.length} ${dataType} exported successfully`,
        });
      }
    } catch (error) {
      console.error(`Error exporting ${dataType}:`, error);
      toast({
        title: "Export Failed",
        description: `Failed to export ${dataType}`,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportObjects = (game: Game) =>
    exportData(apiService.fetchObjects, game, "objects", "Objects");

  const handleExportTemplates = (game: Game) =>
    exportData(apiService.fetchBlueprints, game, "templates", "Templates");

  const handleExportTexts = (game: Game) =>
    exportData(apiService.fetchStringsItems, game, "texts", "Texts");

  const handleExportDocuments = (game: Game) =>
    exportData(apiService.fetchDocumentItems, game, "documents", "Documents");

  return {
    isExporting,
    handleExportObjects,
    handleExportTemplates,
    handleExportTexts,
    handleExportDocuments
  };
}
